import React, { useEffect } from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { loadWorkshopCategory, loadWorkshopHome } from '../../ducks/workshop.duck';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import WorkshopCollection from './WorkshopCollection';
import WorkshopSubnav from './WorkshopSubnav';
import css from './WorkshopPage.module.css';

// Category hero art keyed by the REAL Strapi category slugs (reuse homepage set).
import catHitches from '../../assets/workshop/cat-hitches-towing.jpg';
import catStraps from '../../assets/workshop/cat-straps-tie-downs.jpg';
import catLighting from '../../assets/workshop/cat-lighting-electrical.jpg';
import catTires from '../../assets/workshop/cat-tires-wheels.jpg';
import catToolboxes from '../../assets/workshop/cat-toolboxes-storage.jpg';
import catRamps from '../../assets/workshop/cat-ramps-loading.jpg';
import catSecurity from '../../assets/workshop/cat-security-tracking.jpg';
import catJacks from '../../assets/workshop/cat-jacks-stabilizers.jpg';
import catCovers from '../../assets/workshop/cat-covers-tarps.jpg';
import catMaintenance from '../../assets/workshop/cat-maintenance-safety.jpg';
import catHydraulics from '../../assets/workshop/cat-hydraulics-power.jpg';

const CATEGORY_IMG = {
  'hitches-towing': catHitches,
  'tie-downs-straps': catStraps,
  'lighting-wiring': catLighting,
  'tires-wheels-axles': catTires,
  'storage-toolboxes': catToolboxes,
  'ramps-gates': catRamps,
  'security-protection': catSecurity,
  'jacks-leveling': catJacks,
  'covers-tarps': catCovers,
  'maintenance-safety': catMaintenance,
  'hydraulics-power': catHydraulics,
};

const WorkshopCategoryPageComponent = props => {
  const { scrollingDisabled, config } = props;
  const dispatch = useDispatch();
  const params = useParams();
  const slug = params?.catSlug || '';

  const { currentCategory, categoryAccessories, categoryError, categories } = useSelector(
    state => state.workshop
  );

  useEffect(() => {
    dispatch(loadWorkshopCategory(slug));
  }, [slug]); // eslint-disable-line

  useEffect(() => {
    if (!categories || categories.length === 0) dispatch(loadWorkshopHome());
  }, []); // eslint-disable-line

  const sortedCats = [...(categories || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  const marketplaceName = config?.marketplaceName || 'BigLoads';
  const siteUrl = config?.marketplaceRootURL || 'https://bigloads.com';
  const pageUrl = `${siteUrl}/trailer-accessories/category/${slug}`;

  if (categoryError && !currentCategory) {
    return <NotFoundPage />;
  }

  const c = currentCategory || {};
  const accessories = categoryAccessories || [];
  const catName = c.name || 'Trailer Gear';

  const seoTitle =
    c.seoTitle || `${catName} for Your Trailer (2026) — The Workshop | ${marketplaceName}`;
  const seoDescription =
    c.seoDescription ||
    c.blurb ||
    `Owner-tested ${catName.toLowerCase()} for every trailer. Browse the gear, compare specs, and buy direct.`;

  const subtitle =
    c.blurb ||
    `Find the ${catName.toLowerCase()} you need to keep your trailer road-ready.`;

  // Supporting paragraph under the lede (strip HTML tags if introCopy is rich text).
  const heroBody =
    (c.introCopy ? c.introCopy.replace(/<[^>]+>/g, '') : '') ||
    `Every ${catName.toLowerCase()} pick here is chosen for real trailer work, matched to the rig it fits, and linked straight to the retailer so you pay the same price.`;

  const heroImg = CATEGORY_IMG[slug] || null;

  // ── Structured data (preserved) ─────────────────────────────────────────────
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'The Workshop',
        item: `${siteUrl}/trailer-accessories`,
      },
      { '@type': 'ListItem', position: 3, name: catName, item: pageUrl },
    ],
  };
  const itemListSchema = {
    '@type': 'ItemList',
    name: seoTitle,
    itemListElement: accessories.slice(0, 20).map((a, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: a.name,
      url: `${siteUrl}/trailer-accessories/product/${a.slug}`,
    })),
  };
  const schemas = [breadcrumbSchema, itemListSchema];

  const crumbs = [
    { name: 'Home', to: { name: 'LandingPage' } },
    { name: 'The Workshop', to: { name: 'WorkshopPage' } },
    { name: catName },
  ];

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={seoTitle}
      description={seoDescription}
      canonicalUrl={pageUrl}
      schema={schemas}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={true} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        <WorkshopSubnav categories={sortedCats} css={css} />
        <WorkshopCollection
          accessories={accessories}
          crumbs={crumbs}
          title={catName}
          subtitle={subtitle}
          heroBody={heroBody}
          heroImg={heroImg}
          source="category_page"
          contextSlug={slug}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

WorkshopCategoryPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
};

const EnhancedWorkshopCategoryPage = props => {
  const config = useConfiguration();
  return <WorkshopCategoryPageComponent config={config} {...props} />;
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const WorkshopCategoryPage = compose(connect(mapStateToProps))(EnhancedWorkshopCategoryPage);

// SSR loader (named export for pageDataLoadingAPI)
export const loadData = (params, search, config) => dispatch => {
  const slug = params?.catSlug || '';
  return dispatch(loadWorkshopCategory(slug));
};

export default WorkshopCategoryPage;
